import React from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import { AppContextProvider, AppContextType } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import {
  DatabaseProps,
  HTMLViaNotesProps,
  ProjectTheme,
  TalentTrackerProps,
} from '@interfaces/projects';
import { ContentType, Page, TrackingLocation } from '@tracking';

import ProjectLayout from './ProjectLayout';

interface DatabaseProjectProps<TData, TValue>
  extends AppContextType,
    DatabaseProps<TData, TValue> {}

interface TalentTrackerProjectProps extends AppContextType, TalentTrackerProps {}
interface HTMLViaNotesProjectProps extends AppContextType, HTMLViaNotesProps {}

const queryClient = new QueryClient();

const Project = <TData, TValue>({
  env,
  initialCurrentUser,
  isWebview,
  project,
  ...restProps
}:
  | DatabaseProjectProps<TData, TValue>
  | TalentTrackerProjectProps
  | HTMLViaNotesProjectProps): JSX.Element => {
  const trackingLocation =
    project.theme === ProjectTheme.TalentTracker
      ? TrackingLocation.TalentTracker
      : project.theme === ProjectTheme.HTMLViaNotes
      ? TrackingLocation.HTMLViaNotes
      : TrackingLocation.Database;
  const { Track } = useEventTracking({
    contentId: project.id,
    contentType: ContentType.Project,
    location: trackingLocation,
    page: Page.Project,
    pageId: project.id,
  });

  return (
    <AppContextProvider env={env} initialCurrentUser={initialCurrentUser} isWebview={isWebview}>
      <QueryClientProvider client={queryClient}>
        <Track>
          <ProjectLayout project={project} {...restProps} />
        </Track>
      </QueryClientProvider>
    </AppContextProvider>
  );
};

export default Project;
